import { Injectable, computed, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { InternalChatService } from '../api/internal-chat.service';
import { SseService } from '../api/sse.service';
import { UserServiceService } from '../api/user-service.service';
import { NotificationService } from './toast-service.service';
import {
  InternalChatContact,
  InternalChatConversation,
  InternalChatMessage,
  InternalMessageReceipt,
  InternalMessageStatus,
  InternalMessageMention,
  InternalUserPresence,
} from '../../models/internal-chat.model';

@Injectable({
  providedIn: 'root',
})
export class InternalChatStoreService {
  private readonly messagesPageSize = 20;
  private initialized = false;
  private messagesRequestSeq = 0;
  private conversationsRevision = 0;
  private contactsState = signal<InternalChatContact[]>([]);
  private conversationsState = signal<InternalChatConversation[]>([]);
  private selectedConversationState = signal<InternalChatConversation | null>(
    null,
  );
  private messagesState = signal<InternalChatMessage[]>([]);
  private messagesCurrentPageState = signal<number>(0);
  private hasMoreMessagesState = signal<boolean>(false);
  private isLoadingContactsState = signal<boolean>(false);
  private isLoadingConversationsState = signal<boolean>(false);
  private isLoadingMessagesState = signal<boolean>(false);
  private isLoadingOlderMessagesState = signal<boolean>(false);
  private currentUserIdState = signal<number | null>(null);
  private currentUsernameState = signal<string>('');
  private notifiedInternalMessageIds = new Set<string>();
  private lastReadRequestMessageIds = new Map<number, string>();

  readonly contacts = this.contactsState.asReadonly();
  readonly conversations = this.conversationsState.asReadonly();
  readonly selectedConversation = this.selectedConversationState.asReadonly();
  readonly selectedContact = computed(
    () => this.selectedConversationState()?.contact || null,
  );
  readonly messages = this.messagesState.asReadonly();
  readonly hasMoreMessages = this.hasMoreMessagesState.asReadonly();
  readonly isLoadingContacts = this.isLoadingContactsState.asReadonly();
  readonly isLoadingConversations = this.isLoadingConversationsState.asReadonly();
  readonly isLoadingMessages = this.isLoadingMessagesState.asReadonly();
  readonly isLoadingOlderMessages =
    this.isLoadingOlderMessagesState.asReadonly();
  readonly currentUserId = this.currentUserIdState.asReadonly();
  readonly currentUsername = this.currentUsernameState.asReadonly();
  readonly unreadConversationCount = computed(
    () =>
      this.conversationsState().filter(
        (conversation) => (conversation.unreadCount || 0) > 0,
      ).length,
  );

  constructor(
    private internalChatService: InternalChatService,
    private sseService: SseService,
    private userService: UserServiceService,
    private toastService: NotificationService,
  ) {
    this.sseService.internalMessageReceived.subscribe(
      (message: InternalChatMessage) => {
        this.applyIncomingMessage(message);
      },
    );

    this.sseService.internalMessageStatusReceived.subscribe(
      (status: InternalMessageStatus) => {
        this.applyMessageStatus(status);
      },
    );

    this.sseService.internalUserPresenceReceived.subscribe(
      (presence: InternalUserPresence) => {
        this.applyPresence(presence);
      },
    );

    this.sseService.internalUsersPresenceSnapshotReceived.subscribe(
      (presenceList: InternalUserPresence[]) => {
        this.applyPresenceSnapshot(presenceList);
      },
    );

    this.sseService.reconnected.subscribe(() => {
      this.refreshAll();
      const selected = this.selectedConversationState();
      if (selected && selected.conversationId > 0) {
        this.loadMessages(selected.conversationId, true);
      }
    });
  }

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.sseService.connect();
    this.loadCurrentUser();
    this.refreshAll();
  }

  /**
   * Clears all session-bound chat state so a subsequent login starts with the
   * identity from the new token. The service is application-scoped, therefore
   * it otherwise survives navigating from login back to the home page.
   */
  resetSession(): void {
    this.initialized = false;
    this.messagesRequestSeq++;
    this.contactsState.set([]);
    this.conversationsState.set([]);
    this.selectedConversationState.set(null);
    this.messagesState.set([]);
    this.resetMessagesPagination();
    this.isLoadingContactsState.set(false);
    this.isLoadingConversationsState.set(false);
    this.isLoadingMessagesState.set(false);
    this.isLoadingOlderMessagesState.set(false);
    this.currentUserIdState.set(null);
    this.currentUsernameState.set('');
    this.notifiedInternalMessageIds.clear();
    this.lastReadRequestMessageIds.clear();
  }

  refreshAll(): void {
    const revisionAtRequest = this.conversationsRevision;
    this.isLoadingContactsState.set(true);
    this.isLoadingConversationsState.set(true);

    forkJoin({
      contacts: this.internalChatService.getContacts(),
      conversations: this.internalChatService.getConversations(),
    }).subscribe({
      next: ({ contacts, conversations }) => {
        if (revisionAtRequest !== this.conversationsRevision) {
          this.isLoadingContactsState.set(false);
          this.isLoadingConversationsState.set(false);
          this.refreshAll();
          return;
        }
        const internalContacts = contacts || [];
        this.contactsState.set(internalContacts);
        this.conversationsState.set(
          this.sortConversations(
            this.hydrateConversationsFromContacts(
              conversations || [],
              internalContacts,
            ),
          ),
        );
        this.syncSelectedConversationFromLists();
        this.isLoadingContactsState.set(false);
        this.isLoadingConversationsState.set(false);
      },
      error: (error) => {
        console.error('Error loading internal chats:', error);
        this.isLoadingContactsState.set(false);
        this.isLoadingConversationsState.set(false);
      },
    });
  }

  refreshConversations(): void {
    const revisionAtRequest = this.conversationsRevision;
    this.isLoadingConversationsState.set(true);
    this.internalChatService.getConversations().subscribe({
      next: (conversations) => {
        if (revisionAtRequest !== this.conversationsRevision) {
          this.isLoadingConversationsState.set(false);
          this.refreshConversations();
          return;
        }
        this.conversationsState.set(
          this.sortConversations(
            this.hydrateConversationsFromContacts(
              conversations || [],
              this.contactsState(),
            ),
          ),
        );
        this.syncSelectedConversationFromLists();
        this.isLoadingConversationsState.set(false);
      },
      error: (error) => {
        console.error('Error loading internal conversations:', error);
        this.isLoadingConversationsState.set(false);
      },
    });
  }

  setActiveConversation(conversation: InternalChatConversation | null): void {
    this.selectedConversationState.set(conversation);

    if (!conversation) {
      this.messagesRequestSeq++;
      this.messagesState.set([]);
      this.resetMessagesPagination();
      this.isLoadingMessagesState.set(false);
      return;
    }

    if (conversation.conversationId > 0) {
      this.loadMessages(conversation.conversationId);
      return;
    }

    this.messagesRequestSeq++;
    this.messagesState.set([]);
    this.resetMessagesPagination();
    this.isLoadingMessagesState.set(false);
  }

  setActiveContact(contact: InternalChatContact | null): void {
    if (!contact) {
      this.setActiveConversation(null);
      return;
    }

    const conversation = this.conversationsState().find(
      (item) =>
        item.conversationType === 'INDIVIDUAL' &&
        item.contact?.userId === contact.userId,
    );

    if (!conversation) {
      this.setActiveConversation(this.createDraftIndividualConversation(contact));
      return;
    }

    this.setActiveConversation(conversation);
  }

  clearActiveContact(): void {
    this.setActiveConversation(null);
  }

  clearActiveConversation(): void {
    this.setActiveConversation(null);
  }

  sendMessage(body: string, replyToMessageId: string | null = null, mentions: InternalMessageMention[] = []): Promise<void> {
    const conversation = this.selectedConversationState();
    const normalizedBody = body.trim();

    if (!conversation || !normalizedBody) {
      return Promise.reject(
        new Error('No hay una conversacion interna seleccionada.'),
      );
    }

    return new Promise((resolve, reject) => {
      const sendRequest =
        conversation.conversationType === 'INDIVIDUAL' && conversation.contact
          ? this.internalChatService.sendMessage({
              receiverUserId: conversation.contact.userId,
              body: normalizedBody,
              replyToMessageId,
            })
          : this.internalChatService.sendConversationMessage(
              conversation.conversationId,
              {
                body: normalizedBody,
                replyToMessageId,
                ...(mentions.length ? { mentions } : {}),
              },
            );

      sendRequest.subscribe({
        next: (message) => {
          this.promoteDraftConversationFromMessage(message, conversation);
          this.upsertActiveMessage(message);
          this.upsertConversationFromMessage(message, false);
          resolve();
        },
        error: (error) => reject(error),
      });
    });
  }

  sendMedia(file: File, caption?: string, replyToMessageId: string | null = null, mentions: InternalMessageMention[] = []): Promise<void> {
    const conversation = this.selectedConversationState();
    if (!conversation) return Promise.reject(new Error('No hay una conversacion interna seleccionada.'));
    return new Promise((resolve, reject) => {
      const request = conversation.conversationType === 'INDIVIDUAL' && conversation.contact
        ? this.internalChatService.sendMedia(conversation.contact.userId, file, caption, replyToMessageId)
        : this.internalChatService.sendConversationMedia(conversation.conversationId, file, caption, replyToMessageId, mentions);
      request.subscribe({
        next: message => {
          this.promoteDraftConversationFromMessage(message, conversation);
          this.upsertActiveMessage(message); this.upsertConversationFromMessage(message, false); resolve();
        },
        error: reject,
      });
    });
  }

  acceptAttachmentMessage(message: InternalChatMessage, conversation: InternalChatConversation): void {
    const current = this.selectedConversationState();
    if (current?.conversationId === conversation.conversationId &&
        current?.contact?.userId === conversation.contact?.userId) {
      this.promoteDraftConversationFromMessage(message, conversation);
    }
    const selected = this.selectedConversationState();
    if (selected?.conversationId === message.conversationId) this.upsertActiveMessage(message);
    this.upsertConversationFromMessage(message, false);
  }

  loadOlderMessages(): Promise<boolean> {
    const conversation = this.selectedConversationState();

    if (
      !conversation ||
      !this.hasMoreMessagesState() ||
      this.isLoadingMessagesState() ||
      this.isLoadingOlderMessagesState()
    ) {
      return Promise.resolve(false);
    }

    const nextPage = this.messagesCurrentPageState() + 1;
    return new Promise((resolve, reject) => {
      this.loadMessagesPage(
        conversation.conversationId,
        nextPage,
        true,
        ++this.messagesRequestSeq,
        resolve,
        reject,
      );
    });
  }

  isOutgoingMessage(message: InternalChatMessage): boolean {
    const currentUserId = this.currentUserIdState();
    if (currentUserId !== null) {
      return message.senderUserId === currentUserId;
    }

    const username = this.currentUsernameState();
    return !!username && message.senderUsername === username;
  }

  getMessageReceipts(
    message: InternalChatMessage,
  ): Promise<InternalMessageReceipt[]> {
    if (message.receipts?.length) {
      return Promise.resolve(message.receipts);
    }

    return new Promise((resolve, reject) => {
      this.internalChatService.getMessageReceipts(message.messageId).subscribe({
        next: (receipts) => {
          this.mergeMessageReceipts(message.messageId, receipts || []);
          resolve(receipts || []);
        },
        error: (error) => reject(error),
      });
    });
  }

  private loadCurrentUser(): void {
    this.loadCurrentUserFromSession();

    this.userService.obtenerUsuarioLogeado().subscribe({
      next: (user) => {
        this.currentUserIdState.set(user.id ?? null);
        this.currentUsernameState.set(user.nombreUsuario || '');
      },
      error: () => {
        this.loadCurrentUserFromSession();
      },
    });
  }

  private loadCurrentUserFromSession(): void {
    const username = sessionStorage.getItem('username');
    if (!username) {
      return;
    }
    this.currentUsernameState.set(username);
  }

  private loadMessages(conversationId: number, preserveExisting = false): void {
    const retainCurrentMessages = preserveExisting && this.messagesState().length > 0;
    if (!retainCurrentMessages) {
      this.resetMessagesPagination();
      this.messagesState.set([]);
    }
    this.isLoadingMessagesState.set(true);

    this.loadMessagesPage(
      conversationId,
      0,
      false,
      ++this.messagesRequestSeq,
      undefined,
      undefined,
      retainCurrentMessages,
    );
  }

  private loadMessagesPage(
    conversationId: number,
    page: number,
    prepend: boolean,
    requestSeq = ++this.messagesRequestSeq,
    resolve?: (loadedMore: boolean) => void,
    reject?: (reason?: unknown) => void,
    retainCurrentMessages = false,
  ): void {
    if (prepend) {
      this.isLoadingOlderMessagesState.set(true);
    }

    this.internalChatService
      .getConversationMessagesPage(conversationId, page, this.messagesPageSize)
      .subscribe({
        next: (messagesPage) => {
          const selected = this.selectedConversationState();
          if (
            requestSeq !== this.messagesRequestSeq ||
            !selected ||
            selected.conversationId !== conversationId
          ) {
            resolve?.(false);
            return;
          }

          const content = messagesPage?.content || [];
          if (!retainCurrentMessages) {
            this.messagesCurrentPageState.set(messagesPage?.currentPage ?? page);
            this.hasMoreMessagesState.set(!!messagesPage?.hasNext);
          }

          if (prepend) {
            this.prependMessages(content);
          } else if (retainCurrentMessages) {
            this.messagesState.update((currentMessages) =>
              this.mergeRefreshedMessages(currentMessages, content),
            );
          } else {
            this.messagesState.set(content);
          }

          resolve?.(content.length > 0);
        },
        error: (error) => {
          if (
            requestSeq !== this.messagesRequestSeq ||
            this.selectedConversationState()?.conversationId !== conversationId
          ) {
            resolve?.(false);
            return;
          }
          console.error('Error loading internal messages:', error);
          if (!prepend && !retainCurrentMessages) {
            this.messagesState.set([]);
            this.resetMessagesPagination();
          }
          reject?.(error);
        },
      })
      .add(() => {
        if (requestSeq !== this.messagesRequestSeq) {
          return;
        }

        if (prepend) {
          this.isLoadingOlderMessagesState.set(false);
        } else {
          this.isLoadingMessagesState.set(false);
        }
      });
  }

  private prependMessages(messagesToPrepend: InternalChatMessage[]): void {
    if (messagesToPrepend.length === 0) {
      return;
    }

    this.messagesState.update((messages) => {
      const knownIds = new Set(messages.map((message) => message.messageId));
      const uniqueOlderMessages = messagesToPrepend.filter(
        (message) => !knownIds.has(message.messageId),
      );
      return [...uniqueOlderMessages, ...messages];
    });
  }

  private mergeRefreshedMessages(
    currentMessages: InternalChatMessage[],
    refreshedMessages: InternalChatMessage[],
  ): InternalChatMessage[] {
    const messagesById = new Map<string, InternalChatMessage>();
    currentMessages.forEach((message) => messagesById.set(message.messageId, message));
    refreshedMessages.forEach((message) => {
      messagesById.set(message.messageId, {
        ...messagesById.get(message.messageId),
        ...message,
      });
    });

    return Array.from(messagesById.values()).sort((left, right) => {
      const leftTimestamp = Date.parse(left.timestamp);
      const rightTimestamp = Date.parse(right.timestamp);
      return Number.isFinite(leftTimestamp) && Number.isFinite(rightTimestamp)
        ? leftTimestamp - rightTimestamp
        : 0;
    });
  }

  private resetMessagesPagination(): void {
    this.messagesCurrentPageState.set(0);
    this.hasMoreMessagesState.set(false);
    this.isLoadingOlderMessagesState.set(false);
  }

  private markConversationAsRead(
    conversationId: number,
    requestedMessageId: string,
  ): void {
    this.internalChatService.markConversationByIdAsRead(conversationId).subscribe({
      next: (statuses) => {
        for (const status of statuses || []) {
          this.applyMessageStatus(status);
        }
        this.clearConversationUnread(conversationId);
      },
      error: (error) => {
        if (
          this.lastReadRequestMessageIds.get(conversationId) ===
          requestedMessageId
        ) {
          this.lastReadRequestMessageIds.delete(conversationId);
        }
        console.error('Error marking internal conversation as read:', error);
      },
    });
  }

  markActiveConversationAsRead(): void {
    const conversation = this.selectedConversationState();
    const latestMessage = this.messagesState().at(-1);
    if (
      !conversation ||
      conversation.conversationId <= 0 ||
      !latestMessage?.messageId ||
      this.lastReadRequestMessageIds.get(conversation.conversationId) ===
        latestMessage.messageId
    ) {
      return;
    }

    this.lastReadRequestMessageIds.set(
      conversation.conversationId,
      latestMessage.messageId,
    );
    this.markConversationAsRead(
      conversation.conversationId,
      latestMessage.messageId,
    );
  }

  private applyIncomingMessage(message: InternalChatMessage): void {
    this.conversationsRevision++;
    const selected = this.selectedConversationState();
    const isActive =
      !!selected && selected.conversationId === message.conversationId;
    const isOutgoing = this.isOutgoingMessage(message);

    if (isActive) {
      this.upsertActiveMessage(message);
    } else if (!isOutgoing && this.shouldToastInternalMessage(message)) {
      const mentioned = message.mentions?.some(mention => mention.userId === this.currentUserIdState());
      this.toastService.showInternalToast(
        mentioned
          ? `${this.getMessageSenderLabel(message)} te mencionó en un grupo`
          : `Nuevo mensaje interno de ${this.getMessageSenderLabel(message)}`,
        3500,
      );
    }

    this.upsertConversationFromMessage(message, !isActive && !isOutgoing);
  }

  private applyMessageStatus(status: InternalMessageStatus): void {
    const receipts = status.receipts || [
      {
        userId: status.userId,
        username: status.username,
        nombreCompleto: status.nombreCompleto || status.username,
        delivered: status.delivered,
        deliveredAt: status.deliveredAt,
        read: status.read,
        readAt: status.readAt,
      },
    ];

    this.mergeMessageReceipts(status.messageId, receipts);
  }

  private mergeMessageReceipts(
    messageId: string,
    receipts: InternalMessageReceipt[],
  ): void {
    const mergeMessage = (message: InternalChatMessage): InternalChatMessage => {
      if (message.messageId !== messageId) {
        return message;
      }

      const receiptMap = new Map(
        (message.receipts || []).map((receipt) => [receipt.userId, receipt]),
      );
      for (const receipt of receipts) {
        receiptMap.set(receipt.userId, {
          ...(receiptMap.get(receipt.userId) || {}),
          ...receipt,
        });
      }
      const nextReceipts = Array.from(receiptMap.values());
      const allRead = nextReceipts.length > 0 && nextReceipts.every((r) => r.read);
      const readAt =
        nextReceipts
          .filter((r) => r.readAt)
          .map((r) => r.readAt as string)
          .sort()
          .pop() || message.readAt;

      return {
        ...message,
        receipts: nextReceipts,
        read: allRead,
        readAt,
      };
    };

    this.messagesState.update((messages) => messages.map(mergeMessage));
    this.conversationsState.update((conversations) =>
      conversations.map((conversation) =>
        conversation.lastMessage?.messageId === messageId
          ? {
              ...conversation,
              lastMessage: mergeMessage(conversation.lastMessage),
            }
          : conversation,
      ),
    );
  }

  private applyPresence(presence: InternalUserPresence): void {
    const username = presence.username;
    if (!username) {
      return;
    }

    const updateContact = (contact: InternalChatContact): InternalChatContact =>
      contact.username === username
        ? {
            ...contact,
            connected: presence.connected,
            estadoConexion: presence.estadoConexion ?? contact.estadoConexion,
            modoEstadoConexion: presence.modoEstadoConexion ?? contact.modoEstadoConexion,
          }
        : contact;

    this.contactsState.update((contacts) => contacts.map(updateContact));
    this.conversationsState.update((conversations) =>
      conversations.map((conversation) => ({
        ...conversation,
        contact: conversation.contact ? updateContact(conversation.contact) : null,
      })),
    );

    const selected = this.selectedConversationState();
    if (selected?.contact?.username === username) {
      this.selectedConversationState.set({
        ...selected,
        contact: updateContact(selected.contact),
      });
    }
  }

  private applyPresenceSnapshot(presenceList: InternalUserPresence[]): void {
    for (const presence of presenceList || []) {
      this.applyPresence(presence);
    }
  }

  private shouldToastInternalMessage(message: InternalChatMessage): boolean {
    if (!message.messageId) {
      return true;
    }

    if (this.notifiedInternalMessageIds.has(message.messageId)) {
      return false;
    }

    this.notifiedInternalMessageIds.add(message.messageId);
    return true;
  }

  private upsertActiveMessage(message: InternalChatMessage): void {
    this.messagesState.update((messages) => {
      const exists = messages.some(
        (item) => item.messageId === message.messageId,
      );

      if (exists) {
        return messages.map((item) =>
          item.messageId === message.messageId ? message : item,
        );
      }

      return [...messages, message];
    });
  }

  private upsertConversationFromMessage(
    message: InternalChatMessage,
    incrementUnread: boolean,
  ): void {
    this.conversationsState.update((conversations) => {
      const index = conversations.findIndex(
        (conversation) => conversation.conversationId === message.conversationId,
      );
      const unreadDelta = incrementUnread ? 1 : 0;

      if (index === -1) {
        const selected = this.selectedConversationState();
        const newConversation =
          selected?.conversationId === message.conversationId
            ? {
                ...selected,
                lastMessage: message,
                unreadCount: incrementUnread ? 1 : 0,
              }
            : null;

        if (!newConversation) {
          this.refreshConversations();
          return conversations;
        }

        return this.sortConversations([newConversation, ...conversations]);
      }

      const updated = [...conversations];
      const previous = updated[index];
      updated[index] = {
        ...previous,
        lastMessage: message,
        unreadCount: incrementUnread
          ? (previous.unreadCount || 0) + unreadDelta
          : previous.unreadCount || 0,
      };

      return this.sortConversations(updated);
    });
  }

  private clearConversationUnread(conversationId: number): void {
    this.conversationsRevision++;
    this.conversationsState.update((conversations) =>
      conversations.map((conversation) =>
        conversation.conversationId === conversationId
          ? { ...conversation, unreadCount: 0 }
          : conversation,
      ),
    );
  }

  private syncSelectedConversationFromLists(): void {
    const selected = this.selectedConversationState();
    if (!selected) {
      return;
    }

    const nextSelected = this.conversationsState().find(
      (conversation) => conversation.conversationId === selected.conversationId,
    );

    if (nextSelected) {
      this.selectedConversationState.set(nextSelected);
    }
  }

  private sortConversations(
    conversations: InternalChatConversation[],
  ): InternalChatConversation[] {
    return [...conversations].sort((a, b) => {
      if (a.conversationType !== b.conversationType) {
        return a.conversationType === 'GROUP' ? -1 : 1;
      }

      const aTime = a.lastMessage?.timestamp
        ? new Date(a.lastMessage.timestamp).getTime()
        : 0;
      const bTime = b.lastMessage?.timestamp
        ? new Date(b.lastMessage.timestamp).getTime()
        : 0;

      if (aTime !== bTime) {
        return bTime - aTime;
      }

      return this.getConversationName(a).localeCompare(
        this.getConversationName(b),
        'es',
      );
    });
  }

  private hydrateConversationsFromContacts(
    conversations: InternalChatConversation[],
    contacts: InternalChatContact[],
  ): InternalChatConversation[] {
    return conversations.map((conversation) => {
      if (conversation.conversationType !== 'INDIVIDUAL' || !conversation.contact) {
        return conversation;
      }

      const hydratedContact = contacts.find(
        (contact) =>
          contact.userId === conversation.contact?.userId ||
          contact.username === conversation.contact?.username,
      );

      if (!hydratedContact) {
        return conversation;
      }

      return {
        ...conversation,
        contact: {
          ...conversation.contact,
          ...hydratedContact,
        },
      };
    });
  }

  private getConversationName(conversation: InternalChatConversation): string {
    return (
      conversation.name?.trim() ||
      conversation.contact?.nombreCompleto?.trim() ||
      conversation.contact?.username?.trim() ||
      'Conversacion'
    );
  }

  private getMessageSenderLabel(message: InternalChatMessage): string {
    return (
      message.senderNombreCompleto?.trim() ||
      message.senderUsername?.trim() ||
      'Usuario interno'
    );
  }

  private createDraftIndividualConversation(
    contact: InternalChatContact,
  ): InternalChatConversation {
    return {
      conversationId: 0,
      conversationType: 'INDIVIDUAL',
      name: contact.nombreCompleto || contact.username || 'Usuario',
      members: [
        {
          userId: contact.userId,
          username: contact.username,
          nombreCompleto: contact.nombreCompleto,
        },
      ],
      contact,
      lastMessage: null,
      unreadCount: 0,
    };
  }

  private promoteDraftConversationFromMessage(
    message: InternalChatMessage,
    selectedConversation: InternalChatConversation,
  ): void {
    if (
      selectedConversation.conversationId > 0 ||
      selectedConversation.conversationType !== 'INDIVIDUAL'
    ) {
      return;
    }

    this.selectedConversationState.set({
      ...selectedConversation,
      conversationId: message.conversationId,
      conversationType: message.conversationType,
      lastMessage: message,
    });
  }
}
